import React, { useState } from "react";
import { Grid, Icon, Message, Card, Confirm, Label, Popup, Sidebar, Segment } from "semantic-ui-react";
import SidebarTitle from "components/Sidebar/SidebarTitle";
import { Field, Form, Formik, FieldArray } from "formik";
import CustomInputTextComponent from "components/Form/CustomInputTextComponent";
import { Checkbox } from "components/Form/SpecialButtons";
import Separator from "components/Form/Separator";
import SecondaryButton from "components/Buttons/SecondaryButton";
import MainButton from "components/Buttons/MainButton";
import * as Yup from "yup";
import RedButton from "components/Buttons/RedButton";
import Select from "components/Form/Select";
import { isTabletOrLess } from "utils/isMobile";
import { searchDoctors, postDoctor, patchDoctor } from "services/doctor";
import { searchCompanies } from "services/company";
import { getAllEmergencyReasons } from "services/emergencyReason";
import AsyncFilterSelect from "components/Form/AsyncFilterSelect";
import debounce from "lodash.debounce";
import { connect } from "react-redux";
import EditDoctorModal from "features/FolderManager/components/modals/EditDoctorModal";
import AddCarbonCopie from "components/Form/AddCarbonCopie";
import moment from "moment";
import RadioButtons from "components/Form/RadioButtons";
import CustomDatePickerComponent from "components/Form/CustomDatePickerComponent";
import CustomTimePickerComponent from "components/Form/CustomTimePickerComponent";
import FilePreview from "../FilePreview";
import IconButton from "features/Lab/components/IconButton";
import InputPhone from "components/Form/InputPhone";
import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import { toast } from "react-toastify";
import { formatPhoneNumber, isValidPhoneNumber } from "react-phone-number-input";
import { cleanPhoneNumber, checkPrefixePhoneNumer } from "utils/phone";
import SidebarPatientCard from "components/Sidebar/SidebarPatientCard";
import SidebarTvSec from "components/Sidebar/SidebarTvSec";
import { doctorHandleInputChange } from "utils/selectOptions";
import { withTranslation } from "react-i18next";
import AddNurseHelpModal from "features/FolderManager/components/modals/AddNurseHelpModal";
import BlueButton from "components/Buttons/BlueButton";
import { getSecretariesActive } from "services/folderHelp";

const schemaValidation = (t) => {
	const requiredField = t("editOrAddPrescription.requiredField");
	return Yup.object().shape({
		prescriber: Yup.mixed().when(["no_prescriber", "prescriber_to_create_by_secretary"], {
			is: false,
			then: Yup.mixed().test("requiredTest", t("editOrAddPrescription.requiredPrescripteurDoctor"), (value) => {
				return value.id !== null && value.id !== undefined;
			}),
		}),
		billing_company: Yup.object().shape({
			id: Yup.number().nullable(),
		}),
		reference: Yup.string(),
		// comment: Yup.string(),
		issue_date: Yup.date().when(["no_prescriber", "postdated"], {
			is: false,
			then: Yup.date()
				.nullable()
				.transform((curr, orig) => (orig === "" ? null : curr))
				.required(t("editOrAddPrescription.requiredDatePrescription"))
				.test("dateBefore", t("editOrAddPrescription.requiredDatePrescriptionInf"), (value) => {
					return moment(value).isSameOrBefore(moment(), "date");
				})
				.test("dateBefore6Month", t("editOrAddPrescription.requiredIssueDate1YearBefore"), (value) => {
					const minDate = moment().subtract(12, "months");
					return moment(value).isSameOrAfter(minDate, "date");
				}),
			otherwise: Yup.date().nullable(),
		}),
		validity_date: Yup.date().when(["no_prescriber", "postdated"], {
			is: false,
			then: Yup.date()
				.nullable()
				.transform((curr, orig) => (orig === "" ? null : curr))
				.when(["no_prescriber", "outdated"], (noPrescriber, outdated, schema) => {
					if (noPrescriber === false && outdated === false) {
						return schema
							.required(t("editOrAddPrescription.requiredDateValidity"))
							.test("dateAfterToday", t("editOrAddPrescription.requiredDateValiditySup"), (value) => {
								return !value || moment(value).isSameOrAfter(moment(), "date");
							})
							.test("dateAfterIssueDate", t("editOrAddPrescription.requiredDateValiditySupIssue"), function (value) {
								const { issue_date } = this.parent;
								return !issue_date || moment(value).isSameOrAfter(moment(issue_date), "date");
							})
							.test(
								"dateBefore6MonthsAfterIssueDateNotOutDated",
								t("editOrAddPrescription.requiredDateValiditySup6MonthNotOutdate"),
								function (value) {
									const { issue_date } = this.parent;
									if (!value || !issue_date || moment(issue_date).isAfter(moment(), "date")) return true;
									return moment(issue_date).isSameOrBefore(moment(), "date") && moment().isSameOrBefore(moment(issue_date).add(6, "months"), "date");
								}
							)
							.test("requiredDateValiditySup6Month", t("editOrAddPrescription.requiredDateValiditySup6Month"), function (value) {
								const { issue_date } = this.parent;
								if (!value || !issue_date) return true;
								return moment(value).isSameOrBefore(moment(issue_date).add(6, "months"), "date");
							});
					}

					return schema;
				}),
			otherwise: Yup.date().nullable(),
		}),
		urgent: Yup.bool().required(requiredField),
		to_be_faxed: Yup.bool().required(requiredField),
		to_be_phoned: Yup.bool().required(t(requiredField)),
		to_be_phoned_number: Yup.string()
			.when("to_be_phoned", {
				is: true,
				then: Yup.string().required(t("editOrAddPrescription.requiredPhoneDoctor")),
			})
			.test("phoneValidation", t("editOrAddPrescription.unknownFormatPhone"), (value) => {
				value = cleanPhoneNumber(value);
				return formatPhoneNumber(value) === "" || isValidPhoneNumber(value);
			}),
		to_be_faxed_number: Yup.string()
			.when("to_be_faxed", {
				is: true,
				then: Yup.string().required(t("editOrAddPrescription.requiredDoctorFax")),
			})
			.test("phoneTypeValidation", t("editOrAddPrescription.isNotFax"), (value) => {
				if (formatPhoneNumber(value) !== "") {
					const type = parsePhoneNumberFromString(value).getType();
					return type === "FIXED_LINE" || type === "FIXED_LINE_OR_MOBILE";
				}
				return true;
			})
			.test("phoneValidation", t("editOrAddPrescription.unknownFormatFax"), (value) => {
				value = cleanPhoneNumber(value);
				return formatPhoneNumber(value) === "" || isValidPhoneNumber(value);
			}),
		paper_copy_to_patient: Yup.bool().required(requiredField),
		phone_to_patient: Yup.bool().required(requiredField),
		phone_to_patient_number: Yup.string()
			.when("phone_to_patient", {
				is: true,
				then: Yup.string().required(t("editOrAddPrescription.requiredPhonePatient")),
			})
			.test("phoneValidation", t("editOrAddPrescription.unknownFormatPhone"), (value) => {
				value = cleanPhoneNumber(value);
				return formatPhoneNumber(value) === "" || isValidPhoneNumber(value);
			}),
		fax_to_billing_company: Yup.bool().required(requiredField),
		phone_to_billing_company: Yup.bool().required(requiredField),
		phone_to_billing_company_number: Yup.string()
			.when("phone_to_billing_company", {
				is: true,
				then: Yup.string().required(t("editOrAddPrescription.requiredPhoneCompany")),
			})
			.test("phoneValidation", t("editOrAddPrescription.unknownFormatPhone"), (value) => {
				value = cleanPhoneNumber(value);
				return formatPhoneNumber(value) === "" || isValidPhoneNumber(value);
			}),
		fax_to_billing_company_number: Yup.string()
			.when("fax_to_billing_company", {
				is: true,
				then: Yup.string().required(t("editOrAddPrescription.requiredFaxCompany")),
			})
			.test("phoneTypeValidation", t("editOrAddPrescription.isNotFax"), (value) => {
				if (formatPhoneNumber(value) !== "") {
					const type = parsePhoneNumberFromString(value).getType();
					return type === "FIXED_LINE" || type === "FIXED_LINE_OR_MOBILE";
				}
				return true;
			})
			.test("phoneValidation", t("editOrAddPrescription.unknownFormatFax"), (value) => {
				value = cleanPhoneNumber(value);
				return formatPhoneNumber(value) === "" || isValidPhoneNumber(value);
			}),
		repeatable: Yup.bool().required(requiredField),
		accident: Yup.bool().required(requiredField),
		accident_number: Yup.string().test("accidentFormatValidation", t("editOrAddPrescription.wrongFormatAccidentNumber"), (value) => {
			return !value || value.trim() === "" || new RegExp("^(L|U)([0-9]{9})$").test(value);
		}),
		accident_date: Yup.date().max(moment().format("YYYY-MM-DD"), t("editOrAddPrescription.accidentDateInf")),
		phone_before: Yup.string().nullable(),
		fax_before: Yup.string().nullable(),
		carbonCopyDoctors: Yup.array().of(
			Yup.object()
				.shape({
					doctor: Yup.object().shape({ id: Yup.number() }),
					to_be_faxed: Yup.bool(),
					to_be_phoned: Yup.bool(),
				})
				.nullable()
		),
		paper_copy_to_patient_comment: Yup.string().nullable(),
		home_prescribed: Yup.string().default("false"),
		assignment: Yup.string().nullable(),
	});
};

//Wrapper used to prevent too many re-render of FilePreview iframe
const EditPrescription = (props) => {
	const ConnectedEditPrescriptionForm = withTranslation()(connect(mapStateToProps, mapDispatchToProps)(EditPrescriptionForm));

	const {
		prescription,
		dualScreen,
		validateScanPrescription,
		validateQualityScanPrescription,
		unvalidateQualityScanPrescription,
		deleteScanPrescription,
		submitBarCode,
	} = props;
	return (
		<div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "flex-end" }}>
			{prescription?.scans &&
				!isTabletOrLess() &&
				prescription?.scans.length > 0 &&
				(prescription?.scans.filter((sc) => sc.fromSafeplace && !sc.quality_validated && sc.barcode).length > 0 ||
					!dualScreen?.presentationConnection?.id ||
					(dualScreen?.presentationConnection?.id && (dualScreen?.dashboardDisplay || dualScreen?.patientDisplay))) && (
					<>
						<FilePreview
							validateScanPrescription={validateScanPrescription}
							deleteScanPrescription={deleteScanPrescription}
							validateQualityScanPrescription={validateQualityScanPrescription}
							unvalidateQualityScanPrescription={unvalidateQualityScanPrescription}
							submitBarCode={submitBarCode}
							files={prescription?.scans}
							telesec={props.folder.status === "HELP_NURSE"}
							disabled={props.disabled}
						/>
					</>
				)}
			<ConnectedEditPrescriptionForm {...props} />
		</div>
	);
};

class EditPrescriptionForm extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			numPages: null,
			pageNumber: 1,
			scale: 0.9,
			editDoctorModal: false,
			nurseDoctor: null,
			nurseDoctorToUpdate: null,
			editDoctorAttribute: null,
			height: 0,
			isOutdated: false,
			isPostDated: false,
			disableMyLabConfirmOpen: false,
			deletePrescriptionConfirmOpen: false,
			doctorLoading: false,
			contactLoading: false,
			testPrescriSave: "original",
			helpNurseModal: false,
			isTvSec: false,
			displayAllPatientCard: false,
		};

		this.formBodyRef = React.createRef();

		window.addEventListener("resize", this.updateHeight);
	}

	componentDidMount() {
		this.mounted = true;
		this.props.getAllEmergencyReasons();
		this.updateHeight();
		this.props.getSecretariesActive();
		if (!this.props.documentTypes || !this.props.documentTypes.list || this.props.documentTypes.list.length === 0) this.props.getAllDocumentTypes();
	}
	componentWillUnmount() {
		this.mounted = false;
	}

	componentDidUpdate(prevProps, prevState, snapshot) {}

	updateHeight = () => {
		this.mounted &&
			this.setState({
				height: window.innerHeight,
			});
	};
	onDocumentLoadSuccess = ({ numPages }) => {
		this.mounted && this.setState({ numPages });
	};

	selectBillingCompany = async (searchTerm, setFieldValue) => {
		await this.filterCompanies(searchTerm);
		if (this.props.companies.list && this.props.companies.list.length > 0) {
			setFieldValue(
				"billing_company",
				this.props.companies.list.map((company) => {
					return {
						id: company.id,
						value: company.id,
						label: `${company.type ? `${company.type} - ` : ""} ${company.name} ${company.department ? ` - ${company.department}` : ""}`,
						...company,
					};
				})[0]
			);
		} else toast.error(this.props.t("editOrAddPrescription.undefinedCompany", { searchTerm: searchTerm }));
	};

	selectDoctor = async (searchTerm, setFieldValue) => {
		this.mounted && this.setState({ doctorLoading: true });
		await this.filterDoctors(searchTerm, false);
		if (this.props.doctors.list && this.props.doctors.list.length > 0) {
			setFieldValue("no_prescriber", false);
			setFieldValue(
				"prescriber",
				this.props.doctors.list.map((doctor) => ({
					key: `doctor-${doctor.id}`,
					value: doctor.id,
					id: doctor.id,
					label: `${doctor.code} - ${doctor.last_name} ${doctor.first_name} - ${doctor.speciality || ""} - ${doctor.address?.street} ${
						doctor.address?.postal_code
					} ${doctor.address?.city}`,
					labels: isTabletOrLess()
						? null
						: [
								{ text: doctor.code, width: 10 },
								{ text: doctor.last_name, width: 20 },
								{ text: doctor.first_name, width: 20 },
								{ text: doctor.speciality, width: 10 },
								{ text: doctor.address?.street, width: 20 },
								{ text: doctor.address?.postal_code, width: 5 },
								{ text: doctor.address?.city, width: 15 },
						  ],
					...doctor,
					sil_id: doctor.external_id,
					mylab_id: doctor.id,
				}))[0]
			);
		} else toast.error(this.props.t("editOrAddPrescription.undefinedDoctor", { searchTerm: searchTerm }));
		this.mounted && this.setState({ doctorLoading: false });
	};

	filterDoctors = async (searchTerm, isNotPrescriber) => {
		try {
			await this.props.searchDoctors({ multiple_search: searchTerm }, 20, 0);

			const doctors = this.props.doctors.list
				// Ne pas afficher le doctor 'fax urgent' dans les possibilités pour le prescripteur uniquement
				.filter((doctor) => isNotPrescriber || doctor.external_id !== process.env.REACT_APP_PRESCRIBER_TO_FILTER)
				.map((doctor) => ({
					key: `doctor-${doctor.id}`,
					value: doctor.id,
					id: doctor.id,
					label: isTabletOrLess()
						? `${doctor.code ?? " "}  ${doctor.last_name ?? " "} ${doctor.first_name ?? " "}  ${doctor.speciality ?? " "}  ${
								doctor.address?.street ?? " "
						  } ${doctor.address?.postal_code ?? " "} ${doctor.address?.city ?? " "}`
						: `${doctor.code} - ${doctor.last_name} ${doctor.first_name} - ${doctor.speciality} - ${doctor.address?.street} ${doctor.address?.postal_code} ${doctor.address?.city}`,
					labels: isTabletOrLess()
						? null
						: [
								{ text: doctor.code, width: 10 },
								{ text: doctor.last_name, width: 20 },
								{ text: doctor.first_name, width: 20 },
								{ text: doctor.speciality, width: 10 },
								{ text: doctor.address?.street, width: 20 },
								{ text: doctor.address?.postal_code, width: 5 },
								{ text: doctor.address?.city, width: 15 },
						  ],
					...doctor,
					sil_id: doctor.external_id,
					mylab_id: doctor.id,
				}));

			this.mounted && this.setState({ doctorLoading: false });

			return doctors;
		} catch (error) {
			this.mounted && this.setState({ doctorLoading: false });
			throw error;
		}
	};

	filterCompanies = (searchTerm) => {
		return new Promise((resolve, reject) => {
			this.filterContacts(searchTerm, resolve);
		});
	};

	filterContacts = debounce((searchTerm, callback) => {
		this.props
			.searchCompanies({ name: searchTerm }, 20, 0)
			.then(() => {
				this.mounted && this.setState({ contactLoading: false });
				callback(
					this.props.companies.list
						? this.props.companies.list.map((company) => {
								return {
									id: company.id,
									value: company.id,
									label: `${company.type ? `${company.type} - ` : ""} ${company.name} ${company.department ? ` - ${company.department}` : ""}`,
									...company,
									isDisabled: this.props.access_companies ? this.props.access_companies.filter((ac) => ac.id === company.id).length === 0 : false,
								};
						  })
						: []
				);
			})
			.catch((error) => {
				this.mounted && this.setState({ contactLoading: false });
				callback(error);
			});
	}, 500);

	openEditDoctorModal = (doctor, attribute) => {
		this.mounted &&
			this.setState({
				editDoctorModal: true,
				nurseDoctorToUpdate: doctor || null,
				editDoctorAttribute: attribute,
			});
	};

	closeEditDoctorModal = () => {
		this.mounted &&
			this.setState({
				editDoctorModal: false,
				nurseDoctorToUpdate: null,
				editDoctorAttribute: null,
			});
	};

	submitEditDoctorModal = (values) => {
		// @see NURSE-341 These method's setState() calls to set/unset nurseDoctor are quite unexpected
		this.mounted && this.setState({ nurseDoctor: null });

		return new Promise(async (resolve, reject) => {
			let nurseDoctor = null;

			try {
				// @see NURSE-341 Make sure we never duplicate a doctor, so if there is a `mylab_id` on
				// the local doctor, forgot about its current ID and update expected NURSE ID
				// @see FoldersOverview and FolderManager
				if (values.mylab_id) {
					// @see NURSE-341 `doctors.exists` is populated by this method at reducer stage
					// @see reducers/doctor.js
					await this.props.getDoctorExists(values.mylab_id);
					// This MYLAB doctor does not exist on NURSE
					if (this.props.doctors.exists === 0) {
						// Make sure we do not update an unexpected NURSE doctor
						if (values.id) {
							console.warn("Unexpected NURSE ID#" + values.id + " on MYLAB Doctor#" + values.mylab_id + ", reverted to null (see NURSE-341)");
							values.id = null;
						}
					} else if (parseInt(values.id) !== parseInt(this.props.doctors.exists)) {
						// Warn about this unexpected changes
						console.warn(
							"Unexpected NURSE ID#" +
								values.id +
								" change on MYLAB Doctor#" +
								values.mylab_id +
								", reverted to NURSE ID#" +
								this.props.doctors.exists +
								" (see NURSE-341)"
						);
						// Make sure we assign the correct ID
						values.id = this.props.doctors.exists;
					}
				}

				console.debug("values POST/PATCH", values, this.props.doctors.exists);
				// At this point, we have ensured the association between MYLAB ID and NURSE ID, if any
				if (values.id) {
					await this.props.patchDoctor(values);
					console.warn("[NURSE-341#435] PATCH doctor is disabled now (NURSEID#" + values.id + "; MYLABID#" + values.mylab_id + ")", values);
				} else {
					await this.props.postDoctor(values);
				}

				// @see NURSE-341 `doctors` is populated at reducer stage, reducers/doctor.js
				// With possible failure
				if (this.props.doctors.doctor) nurseDoctor = this.props.doctors.doctor;
			} catch (err) {
				reject(err);
			}

			// @see NURSE-341 These method's setState() calls to set/unset nurseDoctor are quite unexpected
			// Set the created doctor
			nurseDoctor &&
				this.mounted &&
				this.setState({
					nurseDoctor: {
						id: nurseDoctor.id,
						value: nurseDoctor.id,
						data: nurseDoctor,
						label: `${nurseDoctor.code} - ${nurseDoctor.last_name} ${nurseDoctor.first_name} - ${nurseDoctor.city}`,
						isCreated: true,
						attribute: this.state.editDoctorAttribute,
					},
				});

			// @see NURSE-341 These method's setState() calls to set/unset nurseDoctor are quite unexpected
			// Reset the created doctor
			nurseDoctor &&
				this.mounted &&
				this.setState({
					nurseDoctor: null,
				});

			resolve();
		});
	};

	deleteCarbonCopy = (fieldName, values, index, setFieldValue) => {
		values.splice(index, 1);
		setFieldValue(fieldName, values);
	};

	// Check if a prescription is outdated
	isOutDated = (values) =>
		values.validity_date &&
		(moment(values.validity_date).isBefore(moment(), "date") ||
			(values.issue_date && moment().isAfter(moment(values.issue_date).add(6, "month"), "date")));
	isOutdatedPrescription = (values) => {
		let isOutdated = this.isOutDated(values);
		// Only update the state if it changed
		if (this.state.isOutdated !== isOutdated) this.mounted && this.setState({ isOutdated: isOutdated });
	};
	isPostDatedPrescription = (values) => {
		let isPostDated = false;
		isPostDated = moment(values.issue_date).isAfter(moment(), "date");
		if (this.state.isPostDated !== isPostDated) this.mounted && this.setState({ isPostDated: isPostDated });
	};

	fullAddress = (number, street, postalCode, city) => {
		if (!number && !street && !postalCode && !city) return "";
		return ` - ${number || ""} ${street || ""} ${postalCode || ""} ${city || ""}`;
	};

	openDisableMyLabConfirm = () => this.mounted && this.setState({ disableMyLabConfirmOpen: true });

	confirmDisableMyLab = (setFieldValue) => {
		setFieldValue("show_mylab", false);
		setFieldValue("hide_mylab", true);
		setFieldValue("notification_mylab", false);
		this.mounted && this.setState({ disableMyLabConfirmOpen: false });
	};

	cancelDisableMyLab = () => this.setState({ disableMyLabConfirmOpen: false });

	confirmDeletePrescription = (values) => {
		this.props.deletePrescription(values);
		this.mounted && this.setState({ deletePrescriptionConfirmOpen: false });
		this.props.hideSidebar();
	};

	getErrorsPatient = () => {
		let result = [];
		const patient = this.props.patient;
		if (!checkPrefixePhoneNumer(patient.phone)) result.push(this.props.t("editOrAddPrescription.noHomePhonePrefix", { phone: patient.phone }));
		if (!checkPrefixePhoneNumer(patient.mobile_phone))
			result.push(this.props.t("editOrAddPrescription.noPhonePrefix", { phone: patient.mobile_phone }));
		if (!patient.gender) result.push(this.props.t("editOrAddPrescription.requiredSexe"));
		if (!patient.first_name) result.push(this.props.t("editOrAddPrescription.requiredName"));
		if (!patient.birth_name) result.push(this.props.t("editOrAddPrescription.requiredLastName"));
		if (!patient.birth_date) result.push(this.props.t("editOrAddPrescription.requiredBirth"));
		if (patient.home_street) {
			if (!patient.home_postal_code) result.push(this.props.t("editOrAddPrescription.requiredZip"));
			if (!patient.home_city) result.push(this.props.t("editOrAddPrescription.requiredCity"));
			if (!patient.home_country) result.push(this.props.t("editOrAddPrescription.requiredCountry"));
		}
		if (patient.work_street) {
			if (!patient.work_postal_code) result.push(this.props.t("editOrAddPrescription.requiredWorkZip"));
			if (!patient.work_city) result.push(this.props.t("editOrAddPrescription.requiredWorkCity"));
			if (!patient.work_country) result.push(this.props.t("editOrAddPrescription.requiredWorkCountry"));
		}
		return result;
	};

	cancelDeletePrescription = () => this.mounted && this.setState({ deletePrescriptionConfirmOpen: false });

	getHeightPanel = (height, isNewPrescription, showPatientCard, showCommentsTvSec) => {
		//showPatientCard petite = 61 px, grande 165px
		//showCommentsTvSec petite = 36 px, grande 53px
		//bouton supprimer la prescription = 38px
		let { displayAllPatientCard } = this.state;
		let result = height;
		result -= 158; //boutons du bas et titre
		if (showCommentsTvSec) result -= 80;
		if (showPatientCard) result -= displayAllPatientCard ? 180 : 78;
		if (!isNewPrescription) result -= 60; //bouton supprimer la prescription

		return result;
	};

	render() {
		const {
			prescription,
			onSubmit,
			hideSidebar,
			folder,
			emergencyReasons,
			disabled,
			userIsSecretary,
			isSecretaryEditMode,
			patient,
			hideOnSubmit = true,
			generalParameters,
			showPatientCard = false,
			openEditPatient,
			openContactModal,
			openGranteeModal,
			t,
			validationLoading,
		} = this.props;
		const { nurseDoctor, isOutdated, isPostDated } = this.state;

		const isNotOriginal = prescription ? prescription.originalPrescription && prescription.originalPrescription !== null : false;
		const hasTvSecComments = folder?.folderHelp?.comments?.filter((c) => c.manual).length > 0 || false;
		return (
			<div
				style={{
					width: isTabletOrLess() ? "100%" : "50%",
					backgroundColor: "rgb(236, 240, 244)",
					padding: "2rem",
					height: "100%",
				}}
			>
				<Formik
					initialValues={{
						id: prescription?.id,
						prescriber: prescription?.prescriber
							? {
									id: prescription?.prescriber.id,
									value: prescription?.prescriber.mylab_id,
									label: `${prescription?.prescriber.code || ""} - ${prescription?.prescriber.last_name || ""} ${
										prescription?.prescriber.first_name || ""
									} ${prescription?.prescriber.specialty?.name ? ` - ${prescription?.prescriber.specialty?.name}` : ""} ${this.fullAddress(
										prescription?.prescriber.number,
										prescription?.prescriber.street,
										prescription?.prescriber.postalCode,
										prescription?.prescriber.city
									)}`,
									...prescription?.prescriber,
							  }
							: { id: null },
						prescriber_to_create_by_secretary: prescription?.prescriber_to_create_by_secretary ?? false,
						doctorCopy_to_create_by_secretary: prescription?.doctorCopy_to_create_by_secretary ?? false,
						no_prescriber: prescription?.no_prescriber ?? false,
						reference: prescription?.reference || "",
						// comment: prescription?.comment || '',
						reason: prescription?.reason || "",
						issue_date: prescription?.issue_date || null,
						outdated: prescription?.validity_date ? this.isOutDated(prescription) : false,
						postdated: prescription?.issue_date ? !moment(prescription?.issue_date).isSameOrBefore(moment(), "date") : false,
						validity_date: prescription?.validity_date || null,
						urgent: prescription?.urgent ?? false,
						to_be_faxed: prescription?.to_be_faxed ?? false,
						to_be_phoned: prescription?.to_be_phoned ?? false,
						to_be_faxed_number: prescription?.to_be_faxed_number || "",
						to_be_phoned_number: prescription?.to_be_phoned_number || "",
						paper_copy_to_patient: prescription?.paper_copy_to_patient ?? false,
						phone_to_patient: prescription?.phone_to_patient ?? false,
						phone_to_patient_number: prescription?.phone_to_patient_number || "",
						show_mylab: prescription?.show_mylab !== null && prescription?.show_mylab !== undefined ? prescription?.show_mylab : true,
						hide_mylab: prescription?.show_mylab !== null && prescription?.show_mylab !== undefined ? !prescription?.show_mylab : false,
						notification_mylab: prescription?.notification_mylab ?? true,
						fax_to_billing_company: prescription?.fax_to_billing_company ?? false,
						phone_to_billing_company: prescription?.phone_to_billing_company ?? false,
						fax_to_billing_company_number: prescription?.fax_to_billing_company_number || "",
						phone_to_billing_company_number: prescription?.phone_to_billing_company_number || "",
						repeatable: prescription?.repeatable ?? false,
						//iata_active: prescription?.iata_active ?? false,
						//iata_did: prescription?.iata_did || '',
						accident: prescription?.accident ?? false,
						accident_number: prescription?.accident_number?.toUpperCase() || "",
						accident_date: prescription?.accident_date || "",
						provider_barcode: prescription?.provider_barcode,
						folderId: prescription?.folderId,
						phone_before: prescription?.phone_before || null,
						fax_before: prescription?.fax_before || null,
						carbonCopyDoctors:
							prescription?.carbonCopyDoctors && prescription?.carbonCopyDoctors.length > 0
								? prescription?.carbonCopyDoctors.map((cc) => ({
										id: cc.id,
										doctor: cc.doctor
											? {
													id: cc.doctor.id,
													value: cc.doctor.mylab_id,
													label: `${cc.doctor.code || ""} ${cc.doctor.last_name || ""} ${cc.doctor.first_name || ""} - ${
														cc.doctor.specialty?.name || ""
													} - ${cc.doctor.number || ""} ${cc.doctor.street || ""} ${cc.doctor.postal_code || ""} ${cc.doctor.city || ""}`,
													...cc.doctor,
											  }
											: { id: null },
										to_be_faxed: cc.to_be_faxed,
										to_be_phoned: cc.to_be_phoned,
										to_be_faxed_number: cc.to_be_faxed_number || "",
										to_be_phoned_number: cc.to_be_phoned_number || "",
								  }))
								: [],
						billing_company: prescription?.billing_company
							? {
									id: prescription?.billing_company.id,
									value: prescription?.billing_company.id,
									label: `${prescription?.billing_company.type ? `${prescription?.billing_company.type} - ` : ""} ${
										prescription?.billing_company.name
									} ${prescription?.billing_company.department ? ` - ${prescription?.billing_company.department}` : ""}`,
									...prescription?.billing_company,
							  }
							: { id: null },
						paper_copy_to_patient_comment: prescription?.paper_copy_to_patient_comment || null,
						home_prescribed: prescription?.home_prescribed ? "true" : "false",
						assignment: prescription?.assignment || null,
					}}
					validationSchema={schemaValidation(t)}
					onSubmit={(values) => {
						if (hideOnSubmit) hideSidebar();
						values.home_prescribed = values.home_prescribed === "true";
						onSubmit(values);
					}}
				>
					{({ values, setFieldValue, errors, touched, handleSubmit, handleChange, validateForm }) => {
						let errorItems = Object.keys(errors)
							.map((key) => {
								if (touched[key]) return errors[key];
								return null;
							})
							.filter((notNull) => notNull);
						if (
							prescription?.scans &&
							prescription?.scans.length > 0 &&
							prescription?.scans.filter((sc) => sc.fromSafeplace && !sc.quality_validated && !sc.barcode).length > 0
						)
							errorItems.push(t("editOrAddPrescription.invalidOrdonnace"));

						this.isOutdatedPrescription(values);
						this.isPostDatedPrescription(values);

						const isNurseDoctor = values.prescriber?.isCreated || values.prescriber?.value === null || values.prescriber?.data?.isLocal;

						return (
							<Grid style={{ width: "100%" }}>
								<SidebarTitle
									title={t(prescription?.id ? "editOrAddPrescription.edit" : "editOrAddPrescription.add") + " une prescription"}
									subtitle={
										showPatientCard ? (
											<SidebarPatientCard
												patient={patient}
												parameters={generalParameters}
												openEditPatient={() => openEditPatient(values)}
												openContactModal={() => openContactModal(() => openEditPatient(values))}
												openGranteeModal={() => openGranteeModal(() => openEditPatient(values))}
												errorsPatient={this.getErrorsPatient()}
												isTabletOrLess={isTabletOrLess()}
												isTelesec={this.props.folder.status === "HELP_NURSE"}
												toggleDisplayAllPatientCard={() => this.setState({ displayAllPatientCard: !this.state.displayAllPatientCard })}
												displayAllPatientCard={this.state.displayAllPatientCard}
											/>
										) : null
									}
									subtitle2={hasTvSecComments ? <SidebarTvSec comments={this.props.folder.folderHelp?.comments} /> : null}
								/>

								<Grid.Row columns={1}>
									<Grid.Column style={{ padding: 0 }}>
										<Form>
											<div
												style={{
													overflowY: "auto",
													height: this.getHeightPanel(this.state.height, !prescription?.id, showPatientCard, hasTvSecComments),
													marginTop: -15,
													marginBottom: 15,
													width: "100%",
												}}
												ref={this.formBodyRef}
											>
												<Grid className="gridWithNoMarginMobile" style={{ width: "100%" }}>
													{isOutdated && (
														<Grid.Row columns={1} style={{ paddingBottom: 0 }}>
															<Grid.Column style={{ paddingTop: 5, paddingBottom: 0 }}>
																<Message negative>
																	<Message.Header>{t("editOrAddPrescription.expiredPrescription")}</Message.Header>
																</Message>
															</Grid.Column>
														</Grid.Row>
													)}
													{isPostDated && (
														<Grid.Row columns={1}>
															<Grid.Column style={{ paddingTop: 5, paddingBottom: 0 }}>
																<Message negative>
																	<Message.Header>{t("editOrAddPrescription.postDatedPrescription")}</Message.Header>
																</Message>
															</Grid.Column>
														</Grid.Row>
													)}
													<Grid.Row style={{ marginRight: 5, paddingTop: 4 }}>
														<Card
															style={{
																width: "100%",
																marginLeft: 15,
																marginTop: 10,
															}}
														>
															<Card.Header style={{ margin: 5 }}>{t("editOrAddPrescription.general")}</Card.Header>
															<Card.Content>
																<Grid columns={2}>
																	<Grid.Column mobile={16} tablet={16} computer={16}>
																		<Field
																			customlabel={t("editOrAddPrescription.urgent")}
																			component={Checkbox}
																			name="urgent"
																			id="urgent"
																			disabled={disabled || isNotOriginal}
																			onChange={(e) => {
																				handleChange(e);
																				if (values.urgent) setFieldValue("reason", "");
																			}}
																		/>
																		{values.urgent && (
																			<div style={{ marginBottom: 20 }}>
																				<Field
																					component={Select}
																					isCreatable
																					name="reason"
																					customlabel="Motif"
																					options={emergencyReasons.list.map((reason) => {
																						return {
																							value: reason.label,
																							label: reason.label,
																						};
																					})}
																					disabled={disabled || isNotOriginal}
																				/>
																			</div>
																		)}
																	</Grid.Column>
																	<Separator />
																	<Grid.Column mobile={isNurseDoctor ? 14 : 16} tablet={isNurseDoctor ? 14 : 16} computer={isNurseDoctor ? 14 : 16}>
																		<Field
																			component={AsyncFilterSelect}
																			name="prescriber"
																			customlabel={t("editOrAddPrescription.prescriptorDoctor")}
																			// promiseOptions={async (searchTerm) => {
																			// 	this.mounted && this.setState({ doctorLoading: true });
																			// 	return await this.filterDoctors(searchTerm, false);
																			// }}
																			// onInputChange={doctorHandleInputChange}
																			promiseOptions={async (searchTerm) => {
																				this.mounted && this.setState({ doctorLoading: true });
																				return await this.filterDoctors(searchTerm, false);
																			}}
																			onInputChange={doctorHandleInputChange}
																			createdValue={nurseDoctor}
																			required
																			disabled={
																				disabled ||
																				values.no_prescriber ||
																				(userIsSecretary && isSecretaryEditMode && values.prescriber_to_create_by_secretary) ||
																				isNotOriginal
																			}
																			isLoading={this.state.doctorLoading}
																		/>
																	</Grid.Column>
																	{isNurseDoctor && (
																		<Grid.Column mobile={2} tablet={2} computer={2} style={{ marginTop: 23 }}>
																			<IconButton
																				onClick={() => this.openEditDoctorModal(values.prescriber.data, "prescriber")}
																				style={{ marginLeft: 5 }}
																				iconName="edit"
																				disabled={disabled || isNotOriginal}
																			/>
																		</Grid.Column>
																	)}
																	<Grid.Column mobile={16} tablet={8} computer={8}>
																		<Field
																			customlabel={t("editOrAddPrescription.patientWithoutPrescription")}
																			component={Checkbox}
																			name="no_prescriber"
																			id="no_prescriber"
																			onChange={(e) => {
																				handleChange(e);
																				if (!values.no_prescriber) {
																					setFieldValue("prescriber", {
																						id: null,
																					});
																					setFieldValue("outdated", false);
																					setFieldValue("issue_date", null);
																					setFieldValue("validity_date", null);
																					setFieldValue("prescriber_to_create_by_secretary", false);
																					setFieldValue("to_be_phoned", false);
																					setFieldValue("to_be_phoned_number", "");
																					setFieldValue("phone_before", "");
																					setFieldValue("to_be_faxed", false);
																					setFieldValue("to_be_faxed_number", "");
																					setFieldValue("fax_before", "");
																				}
																			}}
																			disabled={disabled || isNotOriginal}
																		/>
																	</Grid.Column>

																	<Grid.Column mobile={16} tablet={8} computer={8}>
																		{/* {!generalParameters.disableFolderSecondValidation && ( */}
																		<Field
																			customlabel={t("editOrAddPrescription.prescriberToCreate")}
																			component={Checkbox}
																			name="prescriber_to_create_by_secretary"
																			id="prescriber_to_create_by_secretary"
																			disabled={disabled || (userIsSecretary && !isSecretaryEditMode) || isNotOriginal}
																			onChange={(e) => {
																				handleChange(e);
																				if (!values.prescriber_to_create_by_secretary) {
																					setFieldValue("prescriber", {
																						id: null,
																					});
																					setFieldValue("no_prescriber", false);
																				}
																			}}
																		/>
																		{/* )} */}
																	</Grid.Column>

																	<Separator />

																	{!values.no_prescriber && (
																		<>
																			<Grid.Column mobile={16} tablet={8} computer={8}>
																				<Field
																					name="issue_date"
																					customlabel={t("editOrAddPrescription.prescriptionDate")}
																					component={CustomDatePickerComponent}
																					required
																					customOnChange={(val) => {
																						if (val) {
																							setFieldValue("issue_date", moment(val).format("YYYY-MM-DD"));
																							setFieldValue("validity_date", moment(val).add(2, "month").format("YYYY-MM-DD"));
																						}
																					}}
																					disabled={disabled || isNotOriginal}
																				/>
																			</Grid.Column>
																			<Grid.Column mobile={16} tablet={8} computer={8}>
																				<Field
																					name="validity_date"
																					customlabel={t("editOrAddPrescription.validityDate")}
																					required
																					component={CustomDatePickerComponent}
																					disabled={disabled || isNotOriginal}
																					onSelect={(val) => {
																						if (val && moment(val).isValid()) {
																							setFieldValue("validity_date", moment(val).format("YYYY-MM-DD"));
																						}
																					}}
																					customOnChange={(val, e) => {
																						if (val && moment(val).isValid()) setFieldValue("validity_date", moment(val).format("YYYY-MM-DD"));
																					}}
																				/>
																				{isOutdated && (
																					<Field
																						customlabel={t("editOrAddPrescription.expiredPrescriptionLabel")}
																						component={Checkbox}
																						name="outdated"
																						id="outdated"
																						disabled={disabled || isNotOriginal}
																					/>
																				)}
																				{!moment(values.issue_date).isSameOrBefore(moment(), "date") && (
																					<Field
																						customlabel={t("editOrAddPrescription.postDatedPrescriptionLabel")}
																						component={Checkbox}
																						name="postdated"
																						id="postdated"
																						disabled={disabled || isNotOriginal}
																					/>
																				)}
																			</Grid.Column>
																		</>
																	)}
																	<Grid.Column mobile={16} tablet={8} computer={8}>
																		<Field
																			type="text"
																			name="reference"
																			customlabel={t("editOrAddPrescription.ref")}
																			placeholder=""
																			component={CustomInputTextComponent}
																			disabled={disabled || isNotOriginal}
																		/>
																	</Grid.Column>
																	<Grid.Column mobile={16} tablet={8} computer={8}>
																		<Field
																			component={AsyncFilterSelect}
																			name="billing_company"
																			customlabel={t("editOrAddPrescription.billingCompanyLabel")}
																			promiseOptions={(searchTerm, callback) => {
																				this.mounted && this.setState({ contactLoading: true });
																				return this.filterContacts(searchTerm, callback);
																			}}
																			isLoading={this.state.contactLoading}
																			placeholder={t("editOrAddPrescription.billingCompanyPlaceHolder")}
																			isClearable={true}
																			disabled={disabled || isNotOriginal}
																			onClear={() =>
																				setFieldValue("billing_company", {
																					id: null,
																				})
																			}
																		/>
																		{!disabled && generalParameters.companyShortcuts && generalParameters.companyShortcuts.split(";").length > 0 && (
																			<div style={{ float: "right", fontSize: "0.8em" }}>
																				{t("editOrAddPrescription.shortcut")}
																				{generalParameters.companyShortcuts
																					.split(";")
																					.filter((s) => s)
																					.map((shortcut) => (
																						<Label
																							key={`shortcut-company-${shortcut}`}
																							as="a"
																							onClick={(e) => {
																								if (e !== undefined) e.preventDefault();
																								this.selectBillingCompany(shortcut, setFieldValue);
																							}}
																						>
																							{shortcut}
																						</Label>
																					))}
																			</div>
																		)}
																	</Grid.Column>
																	<Separator />
																	<Grid.Column mobile={16} tablet={16} computer={16}>
																		<Field
																			customlabel={t("editOrAddPrescription.repeatablePrescription")}
																			component={Checkbox}
																			name="repeatable"
																			id="repeatable"
																			disabled={disabled || isNotOriginal}
																		/>
																	</Grid.Column>
																	{folder?.appointment && folder?.appointment !== null && (
																		<>
																			<Separator />
																			<Grid.Column mobile={16} tablet={8} computer={8}>
																				<RadioButtons
																					name="home_prescribed"
																					field1={{
																						id: "true",
																						customlabel: t("editOrAddPrescription.yes"),
																					}}
																					field2={{
																						id: "false",
																						customlabel: t("editOrAddPrescription.no"),
																					}}
																					customlabel={t("editOrAddPrescription.prescribedAddress")}
																					errors={errors}
																					touched={touched}
																					disabled={disabled || isNotOriginal}
																				/>
																			</Grid.Column>

																			<Grid.Column mobile={16} tablet={8} computer={8}>
																				<Field
																					component={Select}
																					name="assignment"
																					customlabel={t("editOrAddPrescription.appointment")}
																					placeholder={t("editOrAddPrescription.appointmentPlaceHolder")}
																					options={[
																						{
																							value: "ASSIGNED",
																							label: t("editOrAddPrescription.assignedAppointment"),
																						},
																						{
																							value: "DIRECT",
																							label: t("editOrAddPrescription.directAppointment"),
																						},
																					]}
																					disabled={disabled || isNotOriginal}
																				/>
																			</Grid.Column>
																		</>
																	)}
																	<Separator />
																	<Grid.Column mobile={16} tablet={16} computer={16}>
																		<Field
																			customlabel={t("editOrAddPrescription.accident")}
																			component={Checkbox}
																			name="accident"
																			id="accident"
																			disabled={disabled || isNotOriginal}
																			onChange={(e) => {
																				handleChange(e);
																				if (!values.accident) {
																					setFieldValue("accident_number", "");
																					setFieldValue("accident_date", "");
																				}
																			}}
																		/>
																		{values.accident && (
																			<Grid columns={2} style={{ marginBottom: 20 }}>
																				<Grid.Column mobile={16} tablet={8} computer={8}>
																					<Field
																						type="text"
																						name="accident_number"
																						customlabel={t("editOrAddPrescription.accidentNumber")}
																						placeholder=""
																						component={CustomInputTextComponent}
																						required
																						disabled={disabled || isNotOriginal}
																						forceUpperCase
																					/>
																				</Grid.Column>
																				<Grid.Column mobile={16} tablet={8} computer={8}>
																					<Field
																						name="accident_date"
																						customlabel={t("editOrAddPrescription.accidentDate")}
																						max={moment().toDate()}
																						component={CustomDatePickerComponent}
																						required
																						disabled={disabled || isNotOriginal}
																					/>
																				</Grid.Column>
																			</Grid>
																		)}
																	</Grid.Column>
																	<Separator />
																</Grid>
															</Card.Content>
														</Card>
														<Card
															style={{
																width: "100%",
																marginLeft: 15,
															}}
														>
															<Card.Header style={{ margin: 5 }}>{t("editOrAddPrescription.resultCommunicationPrescripteur")}</Card.Header>
															<Card.Content>
																<Grid columns={2}>
																	<Grid.Column mobile={16} tablet={8} computer={8}>
																		<Field
																			customlabel={t("editOrAddPrescription.byPhone")}
																			component={Checkbox}
																			name="to_be_phoned"
																			id="to_be_phoned"
																			disabled={disabled || values.no_prescriber}
																			onChange={(e) => {
																				handleChange(e);
																				setFieldValue("phone_before", "");
																				if (values.to_be_phoned) {
																					setFieldValue("to_be_phoned_number", "");
																				} else {
																					setFieldValue("to_be_phoned_number", cleanPhoneNumber(values.prescriber?.phone));
																				}
																			}}
																		/>
																		{values.to_be_phoned && (
																			<div style={{ marginBottom: 20 }}>
																				<Field
																					component={InputPhone}
																					name="to_be_phoned_number"
																					id="to_be_phoned_number"
																					customlabel={t("editOrAddPrescription.number")}
																					required
																					disabled={disabled || values.no_prescriber}
																				/>
																				<Field
																					name="phone_before"
																					customlabel={t("editOrAddPrescription.before")}
																					placeholder={"8:00"}
																					component={CustomTimePickerComponent}
																					disabled={disabled || values.no_prescriber}
																				/>
																			</div>
																		)}
																	</Grid.Column>
																	<Grid.Column mobile={16} tablet={8} computer={8}>
																		<Field
																			customlabel={t("editOrAddPrescription.byFax")}
																			component={Checkbox}
																			name="to_be_faxed"
																			id="to_be_faxed"
																			disabled={disabled || values.no_prescriber}
																			onChange={(e) => {
																				handleChange(e);
																				setFieldValue("fax_before", "");
																				if (values.to_be_faxed) {
																					setFieldValue("to_be_faxed_number", "");
																				} else {
																					setFieldValue("to_be_faxed_number", cleanPhoneNumber(values.prescriber?.fax));
																				}
																			}}
																		/>
																		{values.to_be_faxed && (
																			<div style={{ marginBottom: 20 }}>
																				<Field
																					component={InputPhone}
																					name="to_be_faxed_number"
																					id="to_be_faxed_number"
																					customlabel={t("editOrAddPrescription.number")}
																					required
																					disabled={disabled || values.no_prescriber}
																				/>
																				<Field
																					name="fax_before"
																					customlabel={t("editOrAddPrescription.before")}
																					placeholder={"8:00"}
																					component={CustomTimePickerComponent}
																					disabled={disabled || values.no_prescriber}
																				/>
																			</div>
																		)}
																	</Grid.Column>
																</Grid>
															</Card.Content>
														</Card>
														<Card
															style={{
																width: "100%",
																marginLeft: 15,
															}}
														>
															<Card.Header style={{ margin: 5 }}>{t("editOrAddPrescription.resultCommunicationPatient")}</Card.Header>
															<Card.Content>
																<Grid columns={2}>
																	{!values.show_mylab && !values.paper_copy_to_patient && (
																		<Grid.Column mobile={16} tablet={16} computer={16} style={{ paddingBottom: 0 }}>
																			<Message warning header="Attention" content={t("editOrAddPrescription.noResultPatient")} />
																		</Grid.Column>
																	)}
																	<Grid.Column mobile={16} tablet={8} computer={8}>
																		<Field
																			customlabel={t("editOrAddPrescription.byPhone")}
																			component={Checkbox}
																			name="phone_to_patient"
																			id="phone_to_patient"
																			disabled={disabled}
																			onChange={(e) => {
																				handleChange(e);
																				if (values.phone_to_patient) {
																					setFieldValue("phone_to_patient_number", "");
																				} else {
																					setFieldValue(
																						"phone_to_patient_number",
																						cleanPhoneNumber(patient.mobile_phone) || cleanPhoneNumber(patient.phone)
																					);
																				}
																			}}
																		/>
																		{values.phone_to_patient && (
																			<div style={{ marginBottom: 20 }}>
																				<Field
																					component={InputPhone}
																					name="phone_to_patient_number"
																					id="phone_to_patient_number"
																					customlabel={t("editOrAddPrescription.number")}
																					required
																					disabled={disabled}
																				/>
																			</div>
																		)}
																	</Grid.Column>
																	<Grid.Column mobile={16} tablet={8} computer={8}>
																		<Field
																			customlabel={t("editOrAddPrescription.paperCopy")}
																			component={Checkbox}
																			name="paper_copy_to_patient"
																			id="paper_copy_to_patient"
																			disabled={disabled}
																		/>
																	</Grid.Column>
																	<Separator />
																	<Grid.Column mobile={16} tablet={16} computer={16} style={{ paddingTop: 0 }}>
																		<Field
																			customlabel={t("editOrAddPrescription.hideResult")}
																			component={Checkbox}
																			name="hide_mylab"
																			id="hide_mylab"
																			disabled={disabled}
																			onChange={(e) => {
																				if (!values.hide_mylab) this.openDisableMyLabConfirm();
																				else {
																					handleChange(e);
																					setFieldValue("show_mylab", true);
																					setFieldValue("notification_mylab", true);
																				}
																			}}
																		/>
																	</Grid.Column>

																	<Separator />
																</Grid>
															</Card.Content>
														</Card>
														<Card
															style={{
																width: "100%",
																marginLeft: 15,
															}}
														>
															<Card.Header style={{ margin: 5 }}>{t("editOrAddPrescription.doctorCopy")}</Card.Header>
															<Card.Content
																style={{
																	marginBottom: values.carbonCopyDoctors.length > 0 ? 20 : 0,
																}}
															>
																<Field
																	customlabel={t("editOrAddPrescription.doctorCopyToCreate")}
																	component={Checkbox}
																	name="doctorCopy_to_create_by_secretary"
																	id="doctorCopy_to_create_by_secretary"
																	disabled={disabled || (userIsSecretary && !isSecretaryEditMode) || isNotOriginal}
																/>
																<AddCarbonCopie
																	customlabel={t("editOrAddPrescription.addDoctorCopy")}
																	onClick={() => {
																		setFieldValue("carbonCopyDoctors", [...values.carbonCopyDoctors, null]);
																	}}
																	disabled={disabled}
																	type="button"
																/>
																<FieldArray
																	name="carbonCopyDoctors"
																	render={() => {
																		return values.carbonCopyDoctors.map((doctor, index) => (
																			<Grid columns={2} key={`ccform-${index}`}>
																				<Separator />
																				<Grid.Column mobile={16} tablet={16} computer={16}>
																					<Field
																						component={AsyncFilterSelect}
																						name={`carbonCopyDoctors.${index}.doctor`}
																						customlabel={t("editOrAddPrescription.addDoctorCopy") + ` (${index + 1})`}
																						promiseOptions={(searchTerm) => {
																							this.mounted &&
																								this.setState({
																									doctorLoading: true,
																								});
																							return this.filterDoctors(searchTerm, true);
																						}}
																						onCreateClick={() => this.openEditDoctorModal(null, `carbonCopyDoctors.${index}.doctor`)}
																						onInputChange={doctorHandleInputChange}
																						createdValue={nurseDoctor}
																						disabled={disabled}
																						customOnChange={(option) => {
																							setFieldValue(`carbonCopyDoctors.${index}.doctor`, option);
																							setFieldValue(`carbonCopyDoctors.${index}.to_be_phoned`, false);
																							setFieldValue(`carbonCopyDoctors.${index}.to_be_phoned_number`, "");
																							setFieldValue(`carbonCopyDoctors.${index}.to_be_faxed`, false);
																							setFieldValue(`carbonCopyDoctors.${index}.to_be_faxed_number`, "");
																						}}
																						isLoading={this.state.doctorLoading}
																					/>
																				</Grid.Column>

																				<Grid.Column mobile={16} tablet={8} computer={8}>
																					<Field
																						customlabel={t("editOrAddPrescription.byPhone")}
																						component={Checkbox}
																						name={`carbonCopyDoctors.${index}.to_be_phoned`}
																						id={`carbonCopyDoctors.${index}.to_be_phoned`}
																						disabled={disabled || !values.carbonCopyDoctors[index]?.doctor}
																						onChange={(e) => {
																							handleChange(e);
																							if (values.carbonCopyDoctors[index]?.to_be_phoned) {
																								setFieldValue(`carbonCopyDoctors.${index}.to_be_phoned_number`, "");
																							} else {
																								setFieldValue(
																									`carbonCopyDoctors.${index}.to_be_phoned_number`,
																									cleanPhoneNumber(values.carbonCopyDoctors[index]?.doctor?.phone)
																								);
																							}
																						}}
																					/>
																					{values.carbonCopyDoctors[index]?.to_be_phoned && (
																						<div style={{ marginBottom: 20 }}>
																							<Field
																								component={InputPhone}
																								name={`carbonCopyDoctors.${index}.to_be_phoned_number`}
																								id={`carbonCopyDoctors.${index}.to_be_phoned_number`}
																								customlabel="Numéro"
																								required
																								disabled={disabled}
																							/>
																						</div>
																					)}
																				</Grid.Column>
																				<Grid.Column mobile={16} tablet={8} computer={8}>
																					<Field
																						customlabel={t("editOrAddPrescription.byFax")}
																						component={Checkbox}
																						name={`carbonCopyDoctors.${index}.to_be_faxed`}
																						id={`carbonCopyDoctors.${index}.to_be_faxed`}
																						disabled={disabled || !values.carbonCopyDoctors[index]?.doctor}
																						onChange={(e) => {
																							handleChange(e);
																							if (values.carbonCopyDoctors[index]?.to_be_faxed) {
																								setFieldValue(`carbonCopyDoctors.${index}.to_be_faxed_number`, "");
																							} else {
																								setFieldValue(
																									`carbonCopyDoctors.${index}.to_be_faxed_number`,
																									cleanPhoneNumber(values.carbonCopyDoctors[index]?.doctor?.fax)
																								);
																							}
																						}}
																					/>
																					{values.carbonCopyDoctors[index]?.to_be_faxed && (
																						<div style={{ marginBottom: 20 }}>
																							<Field
																								component={InputPhone}
																								name={`carbonCopyDoctors.${index}.to_be_faxed_number`}
																								id={`carbonCopyDoctors.${index}.to_be_faxed_number`}
																								customlabel={t("editOrAddPrescription.number")}
																								required
																								disabled={disabled}
																							/>
																						</div>
																					)}
																				</Grid.Column>

																				<Grid.Column mobile={16} tablet={16} computer={16}>
																					<RedButton
																						text={<Icon name="trash" />}
																						type="button"
																						onClick={(e) => {
																							if (e !== undefined) e.preventDefault();
																							this.deleteCarbonCopy("carbonCopyDoctors", values.carbonCopyDoctors, index, setFieldValue);
																						}}
																						style={{ width: "100%" }}
																						disabled={disabled}
																					>
																						{t("editOrAddPrescription.deleteDoctorCopy")}
																					</RedButton>
																				</Grid.Column>
																			</Grid>
																		));
																	}}
																/>
															</Card.Content>
														</Card>
													</Grid.Row>
												</Grid>
											</div>

											<Grid>
												{prescription?.id && (
													<Grid.Row columns={1} style={styles.footerRow}>
														<Grid.Column>
															<RedButton
																text={<Icon name="trash" />}
																type="button"
																onClick={(e) => {
																	if (e !== undefined) e.preventDefault();
																	this.mounted &&
																		this.setState({
																			deletePrescriptionConfirmOpen: true,
																		});
																}}
																style={{ width: "100%" }}
																disabled={disabled}
															>
																{t("editOrAddPrescription.deletePrescription")}
															</RedButton>
														</Grid.Column>
													</Grid.Row>
												)}
												<Separator />
												<Grid.Row columns={3} style={styles.footerRow}>
													<Grid.Column>
														<SecondaryButton
															type="button"
															style={{ width: "100%" }}
															text={t("editOrAddPrescription.cancel")}
															onClick={(e) => {
																if (e !== undefined) e.preventDefault();
																hideSidebar();
															}}
														/>
													</Grid.Column>
													<Grid.Column></Grid.Column>
													<Grid.Column>
														<MainButton
															style={{ width: "100%" }}
															text={t("editOrAddPrescription.confirm")}
															type="submit"
															disabled={disabled || validationLoading}
															onClick={() => {
																this.formBodyRef.current.scrollTop = 0;
																handleSubmit();
															}}
														/>
													</Grid.Column>
												</Grid.Row>
											</Grid>
											<Confirm
												open={this.state.disableMyLabConfirmOpen}
												content={t("editOrAddPrescription.disableMyLabConfirm")}
												cancelButton={t("editOrAddPrescription.no")}
												confirmButton={t("editOrAddPrescription.yes")}
												onCancel={this.cancelDisableMyLab}
												onConfirm={() => this.confirmDisableMyLab(setFieldValue)}
											/>
											<Confirm
												open={this.state.deletePrescriptionConfirmOpen}
												content={t("editOrAddPrescription.deletePrescriptionConfirm")}
												cancelButton={t("editOrAddPrescription.no")}
												confirmButton={t("editOrAddPrescription.yes")}
												onCancel={this.cancelDeletePrescription}
												onConfirm={() => this.confirmDeletePrescription(values)}
											/>
										</Form>
									</Grid.Column>
								</Grid.Row>
							</Grid>
						);
					}}
				</Formik>
				{this.state.editDoctorModal && (
					<EditDoctorModal close={this.closeEditDoctorModal} submit={this.submitEditDoctorModal} doctor={this.state.nurseDoctorToUpdate} />
				)}
			</div>
		);
	}
}

const styles = {
	footerRow: {
		paddingTop: 10,
		marginRight: 5,
		marginLeft: 5,
	},
};

const mapStateToProps = (state) => {
	return {
		doctors: state.doctors,
		companies: state.companies,
		specialties: state.specialties,
		emergencyReasons: state.emergencyReasons,
		generalParameters: state.generalParameters.data,
		center: state.user.session.center,
		access_companies: state.user.session.access_companies,
		secretaries: state.folderHelp.connectedSecretariesList,
		documentTypes: state.documentType,
	};
};

const mapDispatchToProps = {
	searchDoctors,
	postDoctor,
	patchDoctor,
	searchCompanies,
	getAllEmergencyReasons,
	getSecretariesActive,
};

export default EditPrescription;
